<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import cortexModelRoutesAPI from 'dashboard/api/cortexModelRoutes';
import cortexProviderConnectionsAPI from 'dashboard/api/cortexProviderConnections';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextSwitch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const props = defineProps({
  connection: { type: Object, default: null },
});
const emit = defineEmits(['cancel', 'completed']);

const { t } = useI18n();
const isEditMode = computed(() => Boolean(props.connection?.id));
const currentStep = ref(0);
const highestStep = ref(0);
const isLoading = ref(true);
const isSaving = ref(false);
const modelOptionsByProvider = ref({});
const embeddingOptionsByProvider = ref({});
const existingRoutes = ref([]);
const savedConnection = ref(null);
const credentialForms = ref([]);
const removedCredentialIds = ref([]);
const isCredentialEditing = ref(true);
const credentialDeletionDialog = ref(null);
const pendingCredentialDeletion = ref(null);
const hasRoutedConnection = ref(false);
const routedWorkloads = ref([]);
const savedWorkloads = new Set();
const WORKLOADS = [
  'realtime_response',
  'background',
  'media',
  'transcription',
  'embeddings',
  'decision',
];
const workloadsToConfigure = computed(() => {
  if (isEditMode.value) return routedWorkloads.value;

  const configuredWorkloads = new Set(
    existingRoutes.value.map(route => route.workload)
  );
  return WORKLOADS.filter(workload => !configuredWorkloads.has(workload));
});

const connectionForm = reactive({
  name: '',
  provider: 'google',
  quota_scope: 'google-free',
  billing_tier: 'free',
  endpoint: '',
  status: 'active',
});

const selectedModels = reactive({
  primary: '',
  economy: '',
  embedding: '',
});

const steps = computed(() => {
  const items = [
    { key: 'provider', label: t('CORTEX_MODELS.SETUP.STEPS.PROVIDER') },
    { key: 'credentials', label: t('CORTEX_MODELS.SETUP.STEPS.CREDENTIALS') },
  ];
  if (workloadsToConfigure.value.length) {
    items.push({ key: 'models', label: t('CORTEX_MODELS.SETUP.STEPS.MODELS') });
  }
  items.push({ key: 'review', label: t('CORTEX_MODELS.SETUP.STEPS.REVIEW') });
  return items.map((item, number) => ({ ...item, number }));
});
const currentStepKey = computed(() => steps.value[currentStep.value]?.key);

const providerOptions = computed(() => [
  { value: 'google', label: t('CORTEX_MODELS.PROVIDERS.GOOGLE') },
  { value: 'openai', label: t('CORTEX_MODELS.PROVIDERS.OPENAI') },
]);

const billingOptions = computed(() => [
  {
    value: 'free',
    label: t('CORTEX_MODELS.CONNECTIONS.BILLING_OPTIONS.free'),
  },
  {
    value: 'paid',
    label: t('CORTEX_MODELS.CONNECTIONS.BILLING_OPTIONS.paid'),
  },
  {
    value: 'enterprise',
    label: t('CORTEX_MODELS.CONNECTIONS.BILLING_OPTIONS.enterprise'),
  },
  {
    value: 'unknown',
    label: t('CORTEX_MODELS.CONNECTIONS.BILLING_OPTIONS.unknown'),
  },
]);

const modelOptions = computed(
  () => modelOptionsByProvider.value[connectionForm.provider] || []
);
const embeddingOptions = computed(
  () => embeddingOptionsByProvider.value[connectionForm.provider] || []
);

const selectRecommendedModels = () => {
  const defaultModel =
    modelOptions.value.find(option => option.tier === 'default') ||
    modelOptions.value[0];
  const economyModel =
    modelOptions.value.find(option => option.tier === 'low_cost') ||
    defaultModel;

  selectedModels.primary = defaultModel?.value || '';
  selectedModels.economy = economyModel?.value || '';
  selectedModels.embedding = embeddingOptions.value[0]?.value || '';
};

watch(
  () => connectionForm.provider,
  provider => {
    if (isLoading.value) return;
    connectionForm.quota_scope =
      provider === 'google' ? 'google-free' : 'openai-default';
    selectRecommendedModels();
  }
);

const providerStepValid = computed(
  () => connectionForm.name.trim() && connectionForm.quota_scope.trim()
);
const credentialsStepValid = computed(
  () =>
    credentialForms.value.length === 1 &&
    credentialForms.value[0].name.trim() &&
    (credentialForms.value[0].id || credentialForms.value[0].api_key.trim())
);
const usesPrimaryModel = computed(() =>
  workloadsToConfigure.value.some(workload =>
    ['realtime_response', 'media'].includes(workload)
  )
);
const usesEconomyModel = computed(() =>
  workloadsToConfigure.value.some(workload =>
    ['background', 'transcription', 'decision'].includes(workload)
  )
);
const usesEmbeddingModel = computed(() =>
  workloadsToConfigure.value.includes('embeddings')
);
const modelsStepValid = computed(
  () =>
    (!usesPrimaryModel.value || selectedModels.primary) &&
    (!usesEconomyModel.value || selectedModels.economy) &&
    (!usesEmbeddingModel.value || selectedModels.embedding)
);
const canContinue = computed(() => {
  if (currentStepKey.value === 'provider') return providerStepValid.value;
  if (currentStepKey.value === 'credentials') {
    return credentialsStepValid.value;
  }
  if (currentStepKey.value === 'models') return modelsStepValid.value;
  return true;
});

const selectedOptionLabel = (options, value) =>
  options.find(option => option.value === value)?.name ||
  options.find(option => option.value === value)?.label ||
  value;

const providerLabel = computed(
  () =>
    providerOptions.value.find(
      option => option.value === connectionForm.provider
    )?.label
);

const emptyCredential = () => ({
  id: null,
  name: '',
  api_key: '',
  status: 'active',
  masked_api_key: '',
});

const editCredential = () => {
  isCredentialEditing.value = true;
};

const requestCredentialRemoval = credential => {
  if (!credential.id) {
    credentialForms.value = credentialForms.value.filter(
      item => item !== credential
    );
    return;
  }
  pendingCredentialDeletion.value = credential;
  credentialDeletionDialog.value?.open();
};

const confirmCredentialRemoval = () => {
  const credential = pendingCredentialDeletion.value;
  if (!credential) return;
  removedCredentialIds.value.push(credential.id);
  credentialForms.value = [
    {
      ...emptyCredential(),
      name: t('CORTEX_MODELS.SETUP.DEFAULT_CREDENTIAL_NAME'),
    },
  ];
  isCredentialEditing.value = true;
  pendingCredentialDeletion.value = null;
  credentialDeletionDialog.value?.close();
};

const workloadModel = workload => {
  if (workload === 'embeddings') return selectedModels.embedding;
  if (['background', 'transcription', 'decision'].includes(workload)) {
    return selectedModels.economy;
  }
  return selectedModels.primary;
};

const workloadTimeout = workload => {
  if (workload === 'realtime_response') return 30;
  if (workload === 'decision') return 15;
  return 120;
};

const routePayload = (workload, connectionId) => {
  const existing = existingRoutes.value.find(
    route => route.workload === workload
  );
  const existingTargets = existing?.targets || [];
  const matchingTarget = existingTargets.find(
    target => Number(target.provider_connection_id) === Number(connectionId)
  );
  const targets = isEditMode.value
    ? existingTargets.map(target => ({
        provider_connection_id: target.provider_connection_id,
        model:
          Number(target.provider_connection_id) === Number(connectionId)
            ? workloadModel(workload)
            : target.model,
        position: target.position,
        weight: target.weight,
        status: target.status,
        max_input_cost_usd: target.max_input_cost_usd,
        max_output_cost_usd: target.max_output_cost_usd,
      }))
    : [
        {
          provider_connection_id: connectionId,
          model: workloadModel(workload),
          position: 0,
          weight: 100,
          status: 'active',
          max_input_cost_usd: null,
          max_output_cost_usd: null,
        },
      ];

  if (isEditMode.value && !matchingTarget) return null;

  return {
    name: existing?.name || workload,
    workload,
    strategy: 'priority',
    status: 'active',
    max_attempts: 1,
    timeout_seconds: existing?.timeout_seconds || workloadTimeout(workload),
    allow_paid_fallback: false,
    targets,
  };
};

const saveRoute = async (workload, connectionId) => {
  if (savedWorkloads.has(workload)) return;

  const existing = existingRoutes.value.find(
    route => route.workload === workload && !route.inherited
  );
  const modelRoute = routePayload(workload, connectionId);
  if (!modelRoute) return;
  const payload = { model_route: modelRoute };
  if (existing?.id) {
    await cortexModelRoutesAPI.update(existing.id, payload);
  } else {
    await cortexModelRoutesAPI.create(payload);
  }
  savedWorkloads.add(workload);
};

const completeSetup = async () => {
  isSaving.value = true;
  try {
    if (!savedConnection.value) {
      const { data } = await cortexProviderConnectionsAPI.create({
        provider_connection: {
          name: connectionForm.name.trim(),
          provider: connectionForm.provider,
          quota_scope: connectionForm.quota_scope.trim(),
          billing_tier: connectionForm.billing_tier,
          endpoint: connectionForm.endpoint.trim(),
          status: 'active',
        },
      });
      savedConnection.value = data;
    } else if (isEditMode.value) {
      const { data } = await cortexProviderConnectionsAPI.update(
        savedConnection.value.id,
        {
          provider_connection: {
            name: connectionForm.name.trim(),
            quota_scope: connectionForm.quota_scope.trim(),
            billing_tier: connectionForm.billing_tier,
            endpoint: connectionForm.endpoint.trim(),
            status: connectionForm.status,
          },
        }
      );
      savedConnection.value = data;
    }

    await Promise.all(
      removedCredentialIds.value.map(credentialId =>
        cortexProviderConnectionsAPI
          .deleteCredential(savedConnection.value.id, credentialId)
          .then(() => {
            removedCredentialIds.value = removedCredentialIds.value.filter(
              id => id !== credentialId
            );
          })
      )
    );

    await Promise.all(
      credentialForms.value.map(credential => {
        if (credential.id && !isCredentialEditing.value) {
          return Promise.resolve();
        }
        const payload = {
          name: credential.name.trim(),
          status: 'active',
          ...(credential.api_key.trim()
            ? { api_key: credential.api_key.trim() }
            : {}),
        };
        const request = credential.id
          ? cortexProviderConnectionsAPI.updateCredential(
              savedConnection.value.id,
              credential.id,
              payload
            )
          : cortexProviderConnectionsAPI.createCredential(
              savedConnection.value.id,
              payload
            );
        return request.then(({ data }) => {
          const savedCredential = data.credentials.find(
            item => item.name === payload.name
          );
          if (savedCredential) Object.assign(credential, savedCredential);
          credential.api_key = '';
        });
      })
    );

    if (workloadsToConfigure.value.length) {
      await Promise.all(
        workloadsToConfigure.value.map(workload =>
          saveRoute(workload, savedConnection.value.id)
        )
      );
    }

    useAlert(t('CORTEX_MODELS.SETUP.SUCCESS'));
    emit('completed', savedConnection.value);
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.SETUP.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const nextStep = () => {
  if (!canContinue.value) return;
  currentStep.value += 1;
  highestStep.value = Math.max(highestStep.value, currentStep.value);
};

const previousStep = () => {
  if (currentStep.value === 0) {
    emit('cancel');
    return;
  }
  currentStep.value -= 1;
};

const goToStep = step => {
  if (step <= highestStep.value) currentStep.value = step;
};

const loadOptions = async () => {
  isLoading.value = true;
  try {
    const [{ data: settings }, { data: routes }] = await Promise.all([
      cortexSettingsAPI.get(),
      cortexModelRoutesAPI.get(),
    ]);
    modelOptionsByProvider.value =
      settings.model?.model_options_by_provider || {};
    embeddingOptionsByProvider.value =
      settings.model?.embedding_model_options_by_provider || {};
    existingRoutes.value = routes;
    if (isEditMode.value) {
      savedConnection.value = {
        ...props.connection,
        credentials: props.connection.credentials.map(credential => ({
          ...credential,
        })),
      };
      Object.assign(connectionForm, {
        name: props.connection.name,
        provider: props.connection.provider,
        quota_scope: props.connection.quota_scope,
        billing_tier: props.connection.billing_tier,
        endpoint: props.connection.endpoint || '',
        status: props.connection.status,
      });
      selectRecommendedModels();
      const credential = props.connection.credentials[0];
      credentialForms.value = [
        credential
          ? { ...credential, status: 'active', api_key: '' }
          : {
              ...emptyCredential(),
              name: t('CORTEX_MODELS.SETUP.DEFAULT_CREDENTIAL_NAME'),
            },
      ];
      isCredentialEditing.value = !credential;
      routedWorkloads.value = routes
        .filter(route =>
          route.targets.some(
            target =>
              Number(target.provider_connection_id) ===
              Number(props.connection.id)
          )
        )
        .map(route => route.workload);
      hasRoutedConnection.value = routedWorkloads.value.length > 0;

      if (hasRoutedConnection.value) {
        const modelFor = workload =>
          routes
            .find(route => route.workload === workload)
            ?.targets.find(
              target =>
                Number(target.provider_connection_id) ===
                Number(props.connection.id)
            )?.model;
        selectedModels.primary =
          modelFor('realtime_response') ||
          modelFor('media') ||
          selectedModels.primary;
        selectedModels.economy =
          modelFor('background') ||
          modelFor('transcription') ||
          modelFor('decision') ||
          selectedModels.economy;
        selectedModels.embedding =
          modelFor('embeddings') || selectedModels.embedding;
      }
    } else {
      selectRecommendedModels();
      connectionForm.name = t('CORTEX_MODELS.SETUP.DEFAULT_CONNECTION_NAME');
      credentialForms.value = [
        {
          ...emptyCredential(),
          name: t('CORTEX_MODELS.SETUP.DEFAULT_CREDENTIAL_NAME'),
        },
      ];
      isCredentialEditing.value = true;
    }
  } catch (error) {
    useAlert(t('CORTEX_MODELS.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadOptions);
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black1 backdrop-blur-sm"
    >
      <div
        data-testid="cortex-model-setup-wizard-dialog"
        class="flex flex-col w-full max-w-2xl mx-4 overflow-hidden border shadow-xl bg-n-surface-1 rounded-lg border-n-weak max-h-[90vh]"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-wand-sparkles size-5 text-n-brand" />
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{
                isEditMode
                  ? t('CORTEX_MODELS.SETUP.TITLE_EDIT')
                  : t('CORTEX_MODELS.SETUP.TITLE')
              }}
            </h2>
          </div>
          <NextButton
            icon="i-lucide-x"
            slate
            ghost
            sm
            :aria-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
            @click="emit('cancel')"
          />
        </div>

        <div
          class="flex items-center gap-1 px-6 py-3 overflow-x-auto border-b border-n-weak/50 shrink-0"
        >
          <template v-for="(step, index) in steps" :key="step.number">
            <NextButton
              data-testid="cortex-model-setup-wizard-step"
              :disabled="step.number > highestStep"
              slate
              ghost
              sm
              start
              no-animation
              class="!h-auto !px-0 shrink-0 disabled:cursor-default"
              :class="
                currentStep >= step.number
                  ? '!text-n-brand hover:!text-n-brand/80'
                  : '!text-n-slate-10'
              "
              @click="goToStep(step.number)"
            >
              <span
                class="flex items-center justify-center font-bold transition-all rounded-full size-6 text-[10px]"
                :class="
                  currentStep >= step.number
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                <span
                  v-if="currentStep > step.number"
                  class="i-lucide-check size-3.5"
                />
                <template v-else>{{ step.number + 1 }}</template>
              </span>
              <span class="hidden whitespace-nowrap sm:inline">
                {{ step.label }}
              </span>
            </NextButton>
            <span
              v-if="index < steps.length - 1"
              class="flex-1 h-px mx-1 bg-n-weak min-w-2"
            />
          </template>
        </div>

        <div class="flex-1 px-6 py-5 overflow-y-auto">
          <div
            v-if="isLoading"
            class="py-10 text-center text-body-small text-n-slate-11"
          >
            {{ t('CORTEX_MODELS.SETUP.LOADING') }}
          </div>

          <div v-else-if="currentStepKey === 'provider'" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX_MODELS.SETUP.PROVIDER_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX_MODELS.SETUP.PROVIDER_DESCRIPTION') }}
              </p>
            </div>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <WithLabel
                name="setup-provider"
                :label="t('CORTEX_MODELS.CONNECTIONS.PROVIDER')"
                :help-message="
                  t('CORTEX_MODELS.CONNECTIONS.PROVIDER_DESCRIPTION')
                "
              >
                <AdaptiveSelect
                  v-model="connectionForm.provider"
                  :options="providerOptions"
                  :disabled="isEditMode"
                />
              </WithLabel>
              <NextInput
                v-model="connectionForm.name"
                :label="t('CORTEX_MODELS.CONNECTIONS.NAME')"
                :message="t('CORTEX_MODELS.CONNECTIONS.NAME_DESCRIPTION')"
              />
              <NextInput
                v-model="connectionForm.quota_scope"
                :label="t('CORTEX_MODELS.CONNECTIONS.QUOTA_SCOPE')"
                :message="
                  t('CORTEX_MODELS.CONNECTIONS.QUOTA_SCOPE_DESCRIPTION')
                "
              />
              <WithLabel
                name="setup-billing"
                :label="t('CORTEX_MODELS.CONNECTIONS.BILLING')"
                :help-message="
                  t('CORTEX_MODELS.CONNECTIONS.BILLING_DESCRIPTION')
                "
              >
                <AdaptiveSelect
                  v-model="connectionForm.billing_tier"
                  :options="billingOptions"
                />
              </WithLabel>
              <NextInput
                v-model="connectionForm.endpoint"
                class="md:col-span-2"
                :label="t('CORTEX_MODELS.CONNECTIONS.ENDPOINT')"
                :message="t('CORTEX_MODELS.CONNECTIONS.ENDPOINT_DESCRIPTION')"
              />
              <div
                v-if="isEditMode"
                class="flex items-start gap-3 md:col-span-2"
              >
                <NextSwitch
                  :model-value="connectionForm.status === 'active'"
                  @change="
                    connectionForm.status = $event ? 'active' : 'inactive'
                  "
                />
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ t('CORTEX_MODELS.CONNECTIONS.ACTIVE') }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ t('CORTEX_MODELS.CONNECTIONS.ACTIVE_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="currentStepKey === 'credentials'" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX_MODELS.SETUP.CREDENTIALS_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX_MODELS.SETUP.CREDENTIALS_DESCRIPTION') }}
              </p>
            </div>

            <div class="grid gap-3">
              <div
                v-for="credential in credentialForms"
                :key="credential.id || credential"
                class="p-4 border rounded-lg border-n-weak"
              >
                <div
                  v-if="credential.id && !isCredentialEditing"
                  class="flex items-center gap-3"
                >
                  <span
                    class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-2 shrink-0"
                  >
                    <span class="i-lucide-key-round size-4 text-n-slate-11" />
                  </span>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate text-n-slate-12">
                      {{ credential.name }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ credential.masked_api_key }}
                    </p>
                  </div>
                  <div class="flex items-center gap-1 shrink-0">
                    <NextButton
                      data-testid="edit-provider-credential"
                      icon="i-lucide-pencil"
                      slate
                      ghost
                      sm
                      :aria-label="t('CORTEX_MODELS.SETUP.EDIT_CREDENTIAL')"
                      @click="editCredential"
                    />
                    <NextButton
                      data-testid="remove-provider-credential"
                      icon="i-lucide-trash-2"
                      ruby
                      ghost
                      sm
                      :aria-label="t('CORTEX_MODELS.SETUP.REMOVE_CREDENTIAL')"
                      @click="requestCredentialRemoval(credential)"
                    />
                  </div>
                </div>

                <div v-else class="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                  <NextInput
                    v-model="credential.name"
                    :label="t('CORTEX_MODELS.CREDENTIALS.NAME')"
                    :message="t('CORTEX_MODELS.CREDENTIALS.NAME_DESCRIPTION')"
                  />
                  <NextInput
                    v-model="credential.api_key"
                    type="password"
                    :label="t('CORTEX_MODELS.CREDENTIALS.API_KEY')"
                    :placeholder="credential.masked_api_key"
                    :message="
                      credential.id
                        ? t('CORTEX_MODELS.SETUP.EXISTING_KEY_DESCRIPTION')
                        : t('CORTEX_MODELS.CREDENTIALS.API_KEY_DESCRIPTION')
                    "
                  />
                  <NextButton
                    v-if="credential.id"
                    data-testid="remove-provider-credential"
                    icon="i-lucide-trash-2"
                    ruby
                    ghost
                    sm
                    class="md:mt-6"
                    :aria-label="t('CORTEX_MODELS.SETUP.REMOVE_CREDENTIAL')"
                    @click="requestCredentialRemoval(credential)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="currentStepKey === 'models'" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX_MODELS.SETUP.MODELS_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX_MODELS.SETUP.MODELS_DESCRIPTION') }}
              </p>
            </div>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <WithLabel
                v-if="usesPrimaryModel"
                name="setup-primary-model"
                :label="t('CORTEX_MODELS.SETUP.PRIMARY_MODEL')"
                :help-message="
                  t('CORTEX_MODELS.SETUP.PRIMARY_MODEL_DESCRIPTION')
                "
              >
                <AdaptiveSelect
                  v-model="selectedModels.primary"
                  :options="modelOptions"
                  searchable
                />
              </WithLabel>
              <WithLabel
                v-if="usesEconomyModel"
                name="setup-economy-model"
                :label="t('CORTEX_MODELS.SETUP.ECONOMY_MODEL')"
                :help-message="
                  t('CORTEX_MODELS.SETUP.ECONOMY_MODEL_DESCRIPTION')
                "
              >
                <AdaptiveSelect
                  v-model="selectedModels.economy"
                  :options="modelOptions"
                  searchable
                />
              </WithLabel>
              <WithLabel
                v-if="usesEmbeddingModel"
                name="setup-embedding-model"
                :label="t('CORTEX_MODELS.SETUP.EMBEDDING_MODEL')"
                :help-message="
                  t('CORTEX_MODELS.SETUP.EMBEDDING_MODEL_DESCRIPTION')
                "
              >
                <AdaptiveSelect
                  v-model="selectedModels.embedding"
                  :options="embeddingOptions"
                  searchable
                />
              </WithLabel>
            </div>
          </div>

          <div v-else class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX_MODELS.SETUP.REVIEW_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX_MODELS.SETUP.REVIEW_DESCRIPTION') }}
              </p>
            </div>

            <dl class="grid gap-4">
              <div
                class="grid gap-4 pb-4 border-b border-n-weak sm:grid-cols-2"
              >
                <div>
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX_MODELS.SETUP.REVIEW_PROVIDER') }}
                  </dt>
                  <dd class="mt-1 text-sm font-medium text-n-slate-12">
                    {{ providerLabel }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX_MODELS.SETUP.REVIEW_CONNECTION') }}
                  </dt>
                  <dd class="mt-1 text-sm font-medium text-n-slate-12">
                    {{ connectionForm.name }}
                  </dd>
                </div>
              </div>
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX_MODELS.SETUP.REVIEW_CREDENTIALS') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{ credentialForms[0]?.name }}
                  </dd>
                </div>
                <div v-if="usesPrimaryModel">
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX_MODELS.SETUP.REVIEW_PRIMARY') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{
                      selectedOptionLabel(modelOptions, selectedModels.primary)
                    }}
                  </dd>
                </div>
                <div v-if="usesEconomyModel">
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX_MODELS.SETUP.REVIEW_ECONOMY') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{
                      selectedOptionLabel(modelOptions, selectedModels.economy)
                    }}
                  </dd>
                </div>
                <div v-if="usesEmbeddingModel">
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX_MODELS.SETUP.REVIEW_EMBEDDING') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{
                      selectedOptionLabel(
                        embeddingOptions,
                        selectedModels.embedding
                      )
                    }}
                  </dd>
                </div>
              </div>
            </dl>

            <p
              v-if="workloadsToConfigure.length"
              class="text-sm text-n-slate-11"
            >
              {{ t('CORTEX_MODELS.SETUP.ROUTES_DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div
          class="flex items-center justify-between px-6 py-4 border-t border-n-weak shrink-0 bg-n-alpha-1"
        >
          <NextButton
            :label="
              currentStep === 0
                ? t('CORTEX_MODELS.ACTIONS.CANCEL')
                : t('CORTEX_MODELS.SETUP.PREVIOUS')
            "
            icon="i-lucide-arrow-left"
            slate
            ghost
            :disabled="isSaving"
            @click="previousStep"
          />

          <NextButton
            v-if="currentStep < steps.length - 1"
            :label="t('CORTEX_MODELS.SETUP.NEXT')"
            icon="i-lucide-arrow-right"
            trailing-icon
            :disabled="!canContinue || isLoading"
            @click="nextStep"
          />

          <NextButton
            v-else
            :label="
              isEditMode
                ? t('CORTEX_MODELS.SETUP.SAVE')
                : t('CORTEX_MODELS.SETUP.FINISH')
            "
            icon="i-lucide-wand-sparkles"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="completeSetup"
          />
        </div>
      </div>

      <Dialog
        ref="credentialDeletionDialog"
        type="alert"
        :title="t('CORTEX_MODELS.SETUP.REMOVE_CREDENTIAL')"
        :description="t('CORTEX_MODELS.CREDENTIALS.DELETE_CONFIRM')"
        :confirm-button-label="t('CORTEX_MODELS.ACTIONS.DELETE')"
        :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
        @confirm="confirmCredentialRemoval"
        @close="pendingCredentialDeletion = null"
      />
    </div>
  </Teleport>
</template>
